/**
 * The character-level rules a request must satisfy, in one place — and the mechanism
 * that keeps them agreeing with the `CHECK` constraints they mirror.
 *
 * ## Why this file exists
 *
 * Block C shipped seven route files that each spelled these regexes out by hand.
 * `links.ts` alone carried five of them, with a comment above `CHARSET_X` claiming
 * byte-identity with `links_cpv_check` and its two siblings — *"two spellings of one
 * charset is two charsets"*. That claim was true and **nothing checked it**. R7's ruling
 * (`Decisions/2026-08-06 tRPC Ships, and Nothing Is Authored Twice`) says nothing is
 * authored twice, so the regexes move here and the comment becomes a test:
 * `packages/db/test/constraints.test.ts` reads `pg_get_constraintdef()` for every
 * pattern-backed CHECK in the schema and compares it against {@link SQL_PATTERNS}.
 *
 * ## The pattern is authored as SQL, and the JavaScript form is derived
 *
 * This is the part that makes "one definition" true rather than nearly true. The
 * migrations are frozen, hash-verified history and SQL cannot import TypeScript, so
 * literal sharing is impossible in that direction — the honest ceiling is *enforced
 * agreement*. But agreement is only enforceable between **two** things, and a file
 * holding a JavaScript regex *and* the SQL text it corresponds to would hold three: the
 * regex, the SQL constant, and the constraint. Two of those pairs would be checked and
 * one would not.
 *
 * So {@link SQL_PATTERNS} holds the SQL source text — the exact string inside the
 * migration's `~ '…'` — and every JavaScript `RegExp` below is compiled from it by
 * {@link posixToJs}. There is one authored spelling per rule. Editing it moves the
 * validator and reddens the constraint test in the same keystroke, which is the property
 * the comment could never have.
 *
 * ## What `posixToJs` is allowed to do, and why it throws
 *
 * Postgres's `~` takes a POSIX ERE. For every pattern here the two dialects agree
 * character for character except for POSIX bracket expressions — `[:alnum:]` in
 * `links_media_type_check`, which JavaScript does not know. The translation is a small
 * total map, and **an unrecognised class throws at module load** rather than passing the
 * text through unchanged. A silent pass-through would compile
 * `[[:alpha:]]` into a JavaScript class matching the literal characters `:`, `a`, `l`,
 * `p`, `h` — a validator that accepts a colon and rejects nothing it should, wrong in the
 * direction that produces a 500 from the database rather than a 400 from the API.
 *
 * A correction carried here so it is not inherited: `links.ts`'s comment on `MEDIA_TYPE`
 * warned that POSIX `[:alnum:]` excludes the underscore `\w` includes, *"so `text_x/plain`
 * would pass a `\w`-spelled regex here and fail the CHECK."* The general point is right and
 * **that example is not** — the constraint's class is `[[:alnum:]._+-]`, which lists `_`
 * explicitly, so `text_x/plain` is accepted by both spellings. Measured, not reasoned: the
 * derived regex is compared against Postgres's own `~` over a corpus in
 * `constraints.test.ts`, which is the only form of this claim that cannot be wrong in a
 * comment.
 *
 * ## What is NOT here
 *
 * Rules with no CHECK behind them stay ordinary JavaScript regexes ({@link LANGUAGE_TAG},
 * {@link CURIE}, {@link UUID}), and they say so, because the whole value of this file is
 * that a reader can tell which patterns the database is holding them to.
 */
/**
 * The SQL source text of every regex-backed `CHECK` in the schema, keyed by rule.
 *
 * Each string is the literal between the quotes in `column ~ '…'`. `constraints.test.ts`
 * asserts, in both directions:
 *
 *   1. every constraint named in {@link PATTERN_BACKED_CONSTRAINTS} carries exactly this
 *      text in the live database; and
 *   2. every regex-backed CHECK the database actually has is named there — so a future
 *      migration adding one without a shared constant fails rather than drifting.
 */
export const SQL_PATTERNS = {
    /**
     * GS1 charset X, 1–20 characters — the grain's charset for AI 22, AI 10 and AI 21
     * alike. Matched against a scanned value exactly and case-sensitively.
     */
    charsetX: '^[!%-?A-Z_a-z"]{1,20}$',
    /** A GTIN-14 is exactly fourteen digits. Check-digit validity is `packages/gs1`'s. */
    gtin14: "^[0-9]{14}$",
    /** A GS1 company prefix, 4–12 digits. */
    companyPrefix: "^[0-9]{4,12}$",
    /**
     * `http://` or `https://` and nothing else. Lowercase deliberately: this is the layer
     * where a `javascript:` destination is refused rather than diagnosed.
     */
    httpUrl: "^https?://",
    /** A media type such as `text/html`. See the header on `[:alnum:]` versus `\w`. */
    mediaType: "^[[:alnum:]._+-]+/[[:alnum:]._+-]+$",
    /** ISO 3166-1 alpha-2, as `scan_events.country` stores it. */
    countryCode: "^[A-Z]{2}$",
    /**
     * A custom hostname — LDH labels, at least two, each 1–63 characters and neither
     * starting nor ending in a hyphen. Punycode A-labels (`xn--…`) pass unchanged.
     *
     * `0016` deliberately encoded no grammar and said the API would own it, arriving "as a
     * `SQL_PATTERNS` entry with a constraint registered against it". This is that. Case is
     * the database's half already — `domains_hostname_check` compares against `lower()`.
     *
     * An apex (`brand.com`) passes: §8.1 blocks only the unrecoverable, and the screen
     * steers apex to a subdomain rather than refusing it.
     */
    hostname: "^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$",
    /** A usage window, `2026-10` — a calendar month, `TR:770`. */
    usageWindow: "^[0-9]{4}-(0[1-9]|1[0-2])$",
};
/**
 * Which constraint enforces which rule, for every regex-backed CHECK in the schema.
 *
 * Exhaustive by obligation rather than by intent: the constraint test asserts the
 * database has no regex CHECK missing from this map, so the list cannot quietly fall
 * behind a migration.
 */
export const PATTERN_BACKED_CONSTRAINTS = {
    // 0002 — the read model
    resolver_entries_gtin14_check: "gtin14",
    resolver_outbox_gtin14_check: "gtin14",
    // 0003 — scans
    scan_events_gtin14_check: "gtin14",
    scan_events_country_check: "countryCode",
    // 0008 — the trust spine
    company_prefixes_prefix_check: "companyPrefix",
    gtin_claims_gtin14_check: "gtin14",
    // 0009 — the catalogue
    products_gtin14_check: "gtin14",
    product_variants_cpv_check: "charsetX",
    // 0010 — links and recalls
    recalls_gtin14_check: "gtin14",
    links_gtin14_check: "gtin14",
    links_cpv_check: "charsetX",
    links_lot_check: "charsetX",
    links_serial_check: "charsetX",
    links_href_check: "httpUrl",
    links_media_type_check: "mediaType",
    // 0023 — the serving surface's grammar, which 0016 deferred to the API by name
    domains_hostname_grammar_check: "hostname",
    // 0031 — usage metering
    usage_counters_window_check: "usageWindow",
};
/**
 * POSIX bracket-expression classes, spelled as JavaScript character-class contents.
 *
 * Only what the schema uses. Adding a class here is the same act as using it in a
 * migration, and doing one without the other fails loudly — see {@link posixToJs}.
 */
const POSIX_CLASSES = {
    alnum: "A-Za-z0-9",
    alpha: "A-Za-z",
    digit: "0-9",
    lower: "a-z",
    upper: "A-Z",
    space: "\\s",
};
/** Thrown when a SQL pattern names a POSIX class this module cannot translate. */
export class UntranslatablePatternError extends Error {
    name = "UntranslatablePatternError";
}
/**
 * Compile a Postgres `~` pattern into the equivalent JavaScript `RegExp`.
 *
 * Total over the dialect difference that exists in this schema, and loud about the one
 * that does not: an unknown `[:class:]` throws rather than being passed through as
 * literal characters.
 *
 * @throws {UntranslatablePatternError} on a POSIX class outside {@link POSIX_CLASSES}.
 */
export function posixToJs(pattern) {
    const translated = pattern.replaceAll(/\[:([a-z]+):\]/g, (_match, name) => {
        const replacement = POSIX_CLASSES[name];
        if (replacement === undefined) {
            throw new UntranslatablePatternError(`[:${name}:] has no JavaScript spelling in packages/shared/src/api/patterns.ts. ` +
                `Add it to POSIX_CLASSES in the same commit that adds the migration using it — ` +
                `passing it through unchanged would compile a character class matching the ` +
                `letters of the class name, which accepts values the CHECK refuses.`);
        }
        return replacement;
    });
    return new RegExp(translated);
}
/** GS1 charset X, 1–20 characters. Derived from `SQL_PATTERNS.charsetX`. */
export const CHARSET_X = posixToJs(SQL_PATTERNS.charsetX);
/** Fourteen digits. Derived from `SQL_PATTERNS.gtin14`. */
export const GTIN14 = posixToJs(SQL_PATTERNS.gtin14);
/** 4–12 digits. Derived from `SQL_PATTERNS.companyPrefix`. */
export const COMPANY_PREFIX = posixToJs(SQL_PATTERNS.companyPrefix);
/** `http://` or `https://`. Derived from `SQL_PATTERNS.httpUrl`. */
export const HTTP_URL = posixToJs(SQL_PATTERNS.httpUrl);
/** LDH labels, two or more. Derived from `SQL_PATTERNS.hostname`. */
export const HOSTNAME = posixToJs(SQL_PATTERNS.hostname);
/** A media type such as `text/html`. Derived from `SQL_PATTERNS.mediaType`. */
export const MEDIA_TYPE = posixToJs(SQL_PATTERNS.mediaType);
/** ISO 3166-1 alpha-2. Derived from `SQL_PATTERNS.countryCode`. */
export const COUNTRY_CODE = posixToJs(SQL_PATTERNS.countryCode);
// ---------------------------------------------------------------------------
// Rules with no CHECK behind them.
//
// Grouped apart on purpose. Every constant above is held to a constraint by a test;
// these three are held to nothing but this file, and a reader deciding how much to trust
// a validation error should be able to tell which is which at a glance.
// ---------------------------------------------------------------------------
/**
 * BCP 47, structurally — the same shape `entryLinkSchema` requires.
 *
 * `links_hreflang_check` is a cardinality check (`hreflang IS NULL OR cardinality >= 1`),
 * not a pattern, so the *tags themselves* are validated here and nowhere below.
 */
export const LANGUAGE_TAG = /^[A-Za-z]{2,8}(-[A-Za-z0-9]{1,8})*$/;
/**
 * A CURIE such as `gs1:pip`. Shape only — membership of the GS1 namespace is a registry
 * question the route asks of `link_types`, and `links_link_type_fkey` is behind that.
 */
export const CURIE = /^[a-z][A-Za-z0-9]*:[a-z][A-Za-z0-9]*$/;
/**
 * RFC 4122 textual form.
 *
 * Every id column is `uuid`, so the database's own parser is the real gate — this is what
 * turns `/v1/products/nonsense/links` into a 404 instead of an `invalid input syntax for
 * type uuid` raised from inside a query. The version nibble is deliberately unchecked:
 * `gen_random_uuid()` is the documented default for system-inserted rows (§4), so a v4 id
 * is legitimate.
 */
export const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
//# sourceMappingURL=patterns.js.map